// SCR-STAFF-200 — دسته‌ها و خدمات (US-CAT-001، 002، 007): درخت دسته‌ها با نشان حساس (AC-CAT-001.3)، جدول خدمات با جستجو و فیلتر
// وضعیت، قیمت جاری پایه، تعریف دسته و خدمت، و پیوند ورود گروهی (SCR-STAFF-905).
import AddIcon from '@mui/icons-material/Add';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { unwrap, type CategoryView, type ServiceStatus, type ServiceView } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  faDigits,
  ListPage,
  LoadingState,
  Money,
  type DataTableColumn,
} from '@phoenix/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useDeferredValue, useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';
import { useDetailLinkClick, useDetailNavigate } from '../../layout/detailNavigation';
import { WriteNotice } from '../settings/common';
import { CategoryDialog, ServiceCreateDialog } from './CatalogDialogs';
import {
  buildTree,
  catKeys,
  SensitiveBadge,
  servicePath,
  ServiceStatusChip,
  useCategories,
  type CategoryNode,
} from './common';

export const SCR_STAFF_200 = 'SCR-STAFF-200';

/** مسیر صفحه ورود داده (SCR-STAFF-905، AP-PLT-03). */
export const IMPORT_PATH = '/settings/import';

const STATUSES: readonly ServiceStatus[] = ['Draft', 'Active', 'Inactive'];

function CategoryTree({
  nodes,
  selected,
  onSelect,
  onEdit,
  canWrite,
}: {
  nodes: CategoryNode[];
  selected: string | null;
  onSelect: (id: string | null) => void;
  onEdit: (category: CategoryView) => void;
  canWrite: boolean;
}) {
  const { t } = useTranslation('cat');
  const flat: CategoryView[] = [];
  const visit = (node: CategoryNode) => {
    flat.push(node.category);
    node.children.forEach(visit);
  };
  nodes.forEach(visit);

  const render = (category: CategoryView): ReactNode => (
    <ListItem
      key={category.id}
      disablePadding
      secondaryAction={
        canWrite ? (
          <Tooltip title={t('cat:categories.edit')}>
            <IconButton
              edge="end"
              size="small"
              aria-label={t('cat:categories.edit_named', { name: category.name })}
              onClick={() => onEdit(category)}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : undefined
      }
    >
      <ListItemButton
        selected={selected === category.id}
        onClick={() => onSelect(category.id)}
        sx={{ ps: 1 + (Number(category.depth) - 1) * 2.5 }}
        data-testid={`category-${category.code}`}
      >
        <ListItemText
          primary={
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              useFlexGap
              flexWrap="wrap"
              component="span"
            >
              <span>{category.name}</span>
              {category.isSensitiveEffective && <SensitiveBadge inherited={category.inheritsSensitive} />}
            </Stack>
          }
          secondary={t('cat:categories.service_count', { count: faDigits(category.serviceCount) })}
        />
      </ListItemButton>
    </ListItem>
  );

  return (
    <List component="ul" aria-label={t('cat:categories.tree')} dense data-testid="category-tree">
      <ListItem disablePadding>
        <ListItemButton
          selected={selected === null}
          onClick={() => onSelect(null)}
          data-testid="category-all"
        >
          <ListItemText primary={t('cat:categories.all')} />
        </ListItemButton>
      </ListItem>
      {flat.map(render)}
    </List>
  );
}

export function ServicesPage() {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const openDetail = useDetailNavigate();
  const detailClick = useDetailLinkClick();
  const write = useWriteAccess('cat.service.manage');
  const categories = useCategories();
  const [selected, setSelected] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<ServiceStatus | ''>('');
  const deferredQuery = useDeferredValue(query.trim());
  const [categoryDialog, setCategoryDialog] = useState<CategoryView | null | undefined>(undefined);
  const [serviceDialog, setServiceDialog] = useState(false);

  const filter = {
    q: deferredQuery || undefined,
    categoryId: selected ?? undefined,
    status: status || undefined,
  };
  const services = useQuery({
    queryKey: catKeys.services(session?.tenantId, filter),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/catalog/services', {
          params: { query: { ...filter, limit: 200 } },
        }),
      ),
    placeholderData: keepPreviousData,
  });

  const columns: DataTableColumn<ServiceView>[] = [
    { id: 'code', headerKey: 'cat:services.columns.code', cell: (row) => <bdi dir="ltr">{row.code}</bdi> },
    {
      id: 'name',
      headerKey: 'cat:services.columns.name',
      cell: (row) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <RouterLink to={servicePath(row.id)} onClick={detailClick(servicePath(row.id))}>
            {row.name}
          </RouterLink>
          {row.isSensitive && <SensitiveBadge />}
        </Stack>
      ),
    },
    {
      id: 'category',
      headerKey: 'cat:services.columns.category',
      cell: (row) => row.categoryPath.join(' / '),
    },
    { id: 'type', headerKey: 'cat:services.columns.type', cell: (row) => t(`cat:type.${row.type}`) },
    { id: 'unit', headerKey: 'cat:services.columns.unit', cell: (row) => t(`cat:unit.${row.unit}`) },
    {
      id: 'duration',
      headerKey: 'cat:services.columns.duration',
      cell: (row) =>
        t(row.duration.perUnitMinutes ? 'cat:duration.summary_per_unit' : 'cat:duration.summary', {
          main: faDigits(row.duration.durationMinutes),
          perUnit: faDigits(row.duration.perUnitMinutes ?? 0),
        }),
    },
    {
      id: 'price',
      headerKey: 'cat:services.columns.price',
      cell: (row) =>
        row.currentPrice ? (
          <Money amountMinor={row.currentPrice.amount} currency={row.currentPrice.currency} />
        ) : (
          <Typography component="span" variant="body2" color="text.secondary">
            {t('cat:prices.not_defined')}
          </Typography>
        ),
    },
    {
      id: 'status',
      headerKey: 'cat:services.columns.status',
      cell: (row) => <ServiceStatusChip status={row.status} />,
    },
  ];

  const allCategories = categories.data?.items ?? [];

  return (
    <ListPage
      screenId={SCR_STAFF_200}
      titleKey="cat:services.title"
      subtitleKey="cat:services.subtitle"
      actions={
        <>
          <Button
            component={RouterLink}
            to={IMPORT_PATH}
            startIcon={<UploadFileOutlinedIcon />}
            data-testid="services-import"
          >
            {t('cat:services.import')}
          </Button>
          <Button
            startIcon={<CreateNewFolderOutlinedIcon />}
            onClick={() => setCategoryDialog(null)}
            disabled={!write.canWrite}
            data-testid="category-create"
          >
            {t('cat:categories.add')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setServiceDialog(true)}
            disabled={!write.canWrite || allCategories.length === 0}
            data-testid="service-create"
          >
            {t('cat:services.add')}
          </Button>
        </>
      }
      filters={<WriteNotice write={write} />}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
        <Paper
          variant="outlined"
          sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0 }}
          component="nav"
          aria-label={t('cat:categories.tree')}
        >
          <Typography variant="subtitle2" sx={{ px: 2, pt: 1.5 }}>
            {t('cat:categories.title')}
          </Typography>
          {categories.isPending ? (
            <LoadingState rows={3} />
          ) : categories.error ? (
            <ErrorState error={categories.error} onRetry={() => void categories.refetch()} compact />
          ) : (
            <CategoryTree
              nodes={buildTree(allCategories)}
              selected={selected}
              onSelect={setSelected}
              onEdit={(category) => setCategoryDialog(category)}
              canWrite={write.canWrite}
            />
          )}
        </Paper>
        <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
          <DataTable
            captionKey="cat:services.caption"
            columns={columns}
            rows={services.data?.items}
            getRowId={(row) => row.id}
            isLoading={services.isPending}
            error={services.error}
            onRetry={() => void services.refetch()}
            onRowClick={(row) => openDetail(servicePath(row.id))}
            emptyTitleKey={
              allCategories.length === 0 ? 'cat:services.empty_no_categories' : 'cat:services.empty'
            }
            toolbar={
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ p: 1.5 }}>
                <TextField
                  size="small"
                  label={t('cat:services.search')}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  slotProps={{ htmlInput: { 'data-testid': 'services-search' } }}
                  sx={{ minWidth: 240 }}
                />
                <TextField
                  select
                  size="small"
                  label={t('cat:services.status_filter')}
                  value={status}
                  onChange={(event) => setStatus(event.target.value as ServiceStatus | '')}
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="">{t('cat:services.all_statuses')}</MenuItem>
                  {STATUSES.map((value) => (
                    <MenuItem key={value} value={value}>
                      {t(`cat:status.${value}`)}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            }
          />
        </Box>
      </Stack>
      {categoryDialog !== undefined && (
        <CategoryDialog
          category={categoryDialog}
          defaultParentId={selected}
          categories={allCategories}
          onClose={() => setCategoryDialog(undefined)}
        />
      )}
      {serviceDialog && (
        <ServiceCreateDialog
          categories={allCategories}
          defaultCategoryId={selected}
          onClose={() => setServiceDialog(false)}
        />
      )}
    </ListPage>
  );
}
